/*
This file is located in the main branch
Last time the file was updated (UTC): [11/6/2024 4:35:58AM]
Permissions to update outside the main branch: [false]
*/

import {
    DocumentTitle,
    Form,
    ModalConfirm,
    requiredRule,
    useCondition,
    useErrorState,
    ValidatedInput,
} from '@finbeat/react-toolkit';
import { forgotPassword } from 'axios/accountsApi';
import Window from 'components/Window';
import useApi from 'hooks/useApi';
import { useGeneralSettingsContext } from 'providers/GeneralSettingsProvider';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { UseMutateFunction, useMutation } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { Button, Card, CardBody, Col, Row } from 'reactstrap';

type ForgotPasswordType = {
    login: string;
};

const ForgotPassword = () => {
    const api = useApi();

    const { designType } = useGeneralSettingsContext();

    const { t } = useTranslation();
    const { handleRequestError } = useErrorState();
    const [isOpenNotify, setIsOpenNotify] = useState(false);

    const { isLoading, mutate } = useMutation((login: string) => forgotPassword(api, login), {
        onSuccess: () => setIsOpenNotify(true),
        onError: (e) => handleRequestError(e),
    });

    const Content = useCondition(
        designType,
        {
            nova: NovaForgotPasswordContent,
        },
        ClassicForgotPasswordContent
    );

    return (
        <DocumentTitle code="BackOfficePasswordReset">
            <Content mutate={mutate} isLoading={isLoading} />

            <ModalConfirm
                content={t('BackOfficeResetPasswordLinkSend')}
                confirmButtonContent={t('BackOfficeOk')}
                isOpen={isOpenNotify}
                toggle={() => setIsOpenNotify(false)}
                isVisibleCancelButton={false}
            />
        </DocumentTitle>
    );
};

type ForgotPasswordContentProps = {
    mutate: UseMutateFunction<any, unknown, string, unknown>;
    isLoading: boolean;
};

const NovaForgotPasswordContent = ({ mutate, isLoading }: ForgotPasswordContentProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    return (
        <Window<ForgotPasswordType>
            onSubmit={(data) => mutate(data.login)}
            label={t('BackOfficeInputLoginOrEmail')}
        >
            <ValidatedInput<ForgotPasswordType>
                data-testid="input-login"
                name="login"
                rules={{ required: requiredRule }}
                isFloating={false}
                loading={isLoading}
            />
            <Button type="submit" color="primary">
                <span>
                    <Trans i18nKey="BackOfficeSend" />
                </span>
            </Button>
            <div className="flex-centered">
                <button
                    className="window__link-text-btn btn-container cursor--pointer"
                    onClick={() => navigate('/login')}
                >
                    <Trans i18nKey="BackOfficeBack" />
                </button>
            </div>
        </Window>
    );
};

const ClassicForgotPasswordContent = ({ mutate, isLoading }: ForgotPasswordContentProps) => {
    return (
        <div>
            <Row>
                <Col sm="3" />
                <Col sm="6">
                    <Card>
                        <CardBody>
                            <Form<ForgotPasswordType>
                                clearAfterSubmit={false}
                                onSubmit={(data) => mutate(data.login)}
                            >
                                <Trans i18nKey="BackOfficeInputLoginOrEmail" />
                                <ValidatedInput<ForgotPasswordType>
                                    data-testid="input-login"
                                    name="login"
                                    rules={{ required: requiredRule }}
                                    isFloating={false}
                                    loading={isLoading}
                                />
                                <Button type="submit">
                                    <span>
                                        <Trans i18nKey="BackOfficeSend" />
                                    </span>
                                </Button>
                            </Form>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default ForgotPassword;
